import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, Segmented, Select, Table, Tabs } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Download, Search } from 'lucide-react';
import Layout from '../../Layout';
import { useKho } from './khoStore';
import {
  DOC_STATUS, DOC_TYPE, docValue, fmtMoney, inTransit, itemById, serviceById, supplierById, whById, type DocType, type KhoState, type StockDoc,
} from './khoLogic';
import { CreateDocButton, DocStatusTag, DocTypeTag, PageHeader, docUrl, exportXlsx, fmtDate } from './khoUi';

type Tab = 'all' | 'draft' | 'pending' | 'transit' | 'approved' | 'closed';
const TABS: { key: Tab; label: string; match: (d: StockDoc) => boolean }[] = [
  { key: 'all', label: 'Tất cả', match: () => true },
  { key: 'pending', label: 'Chờ duyệt', match: d => d.status === 'pending' },
  { key: 'transit', label: 'Đang chuyển', match: inTransit },
  { key: 'draft', label: 'Nháp', match: d => d.status === 'draft' },
  { key: 'approved', label: 'Đã duyệt', match: d => d.status === 'approved' && !inTransit(d) },
  { key: 'closed', label: 'Từ chối / Hủy', match: d => d.status === 'rejected' || d.status === 'cancelled' },
];
const RANGES = { '7': 7, '30': 30, '90': 90, all: 0 } as const;

/** Đối tượng của phiếu: NCC, khách + dịch vụ, kho nhận, lý do hủy */
export function docParty(s: KhoState, d: StockDoc) {
  if (d.type === 'import') return supplierById(s, d.supplierId)?.name || '—';
  if (d.type === 'transfer') return `→ ${whById(s, d.toWarehouseId)?.name || '—'}`;
  if (d.type === 'dispose') return d.reason || '—';
  if (d.type === 'count') return d.note || 'Kiểm kê định kỳ';
  const svc = serviceById(s, d.serviceId)?.name;
  return [d.customer?.name, svc].filter(Boolean).join(' · ') || '—';
}

/** Danh sách phiếu kho (nhập / xuất / chuyển / hủy / kiểm) */
export default function DocsPage({ fixedType }: { fixedType?: DocType }) {
  const s = useKho();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'all';
  const type = fixedType || (params.get('type') as DocType) || null;
  const wh = params.get('wh') || null;
  const q = params.get('q') || '';
  const range = (params.get('range') as keyof typeof RANGES) || '30';
  const go = (p: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };

  const base = useMemo(() => {
    const from = RANGES[range] ? dayjs().subtract(RANGES[range], 'day').startOf('day').valueOf() : 0;
    const k = q.trim().toLowerCase();
    return s.docs.filter(d => {
      if (type && d.type !== type) return false;
      if (wh && d.warehouseId !== wh && d.toWarehouseId !== wh) return false;
      // Phiếu đang chờ xử lý luôn hiện, không bị giới hạn bởi khoảng thời gian
      if (from && d.date < from && d.status !== 'pending' && d.status !== 'draft' && !inTransit(d)) return false;
      if (k) {
        const hay = [d.code, d.refCode, d.customer?.name, d.customer?.phone, docParty(s, d), ...d.lines.map(l => `${itemById(s, l.itemId)?.name} ${l.lotNo}`)].join(' ').toLowerCase();
        if (!hay.includes(k)) return false;
      }
      return true;
    }).sort((a, b) => b.date - a.date);
  }, [s, type, wh, q, range]);

  const rows = base.filter(TABS.find(t => t.key === tab)!.match);

  const columns: ColumnsType<StockDoc> = [
    { title: 'Mã phiếu', key: 'code', width: 150, render: (_, d) => <span className="font-medium text-[#3e91ab]">{d.code}</span> },
    ...(!fixedType ? [{ title: 'Loại', key: 'type', width: 150, render: (_: unknown, d: StockDoc) => <DocTypeTag type={d.type} /> }] : []),
    { title: 'Ngày', key: 'date', width: 105, render: (_, d) => fmtDate(d.date) },
    { title: 'Kho', key: 'wh', width: 190, render: (_, d) => whById(s, d.warehouseId)?.name },
    { title: 'Đối tượng', key: 'party', render: (_, d) => <div className="max-w-[260px] truncate" title={docParty(s, d)}>{docParty(s, d)}</div> },
    {
      title: 'Mặt hàng', key: 'items', render: (_, d) => {
        const names = Array.from(new Set(d.lines.map(l => itemById(s, l.itemId)?.name).filter(Boolean)));
        return <div className="max-w-[260px] truncate text-gray-600 dark:text-gray-300" title={names.join(', ')}>{names.length} · {names.slice(0, 2).join(', ')}{names.length > 2 ? '…' : ''}</div>;
      },
    },
    { title: fixedType === 'count' ? 'Giá trị lệch' : 'Giá trị', key: 'value', width: 140, align: 'right', render: (_, d) => fmtMoney(docValue(d)) },
    { title: 'Trạng thái', key: 'status', width: 120, render: (_, d) => <DocStatusTag doc={d} /> },
    { title: 'Người lập', key: 'by', width: 170, render: (_, d) => <span className="text-gray-500 text-xs">{d.createdBy}</span> },
  ];

  const doExport = () => exportXlsx(fixedType === 'count' ? 'phieu_kiem_kho' : 'phieu_kho', 'Phiếu kho', rows.map(d => ({
    'Mã phiếu': d.code, 'Loại': DOC_TYPE[d.type].label, 'Ngày': fmtDate(d.date), 'Kho': whById(s, d.warehouseId)?.name || '',
    'Đối tượng': docParty(s, d), 'Số mặt hàng': d.lines.length, 'Giá trị': Math.round(docValue(d)),
    'Trạng thái': inTransit(d) ? 'Đang chuyển' : DOC_STATUS[d.status].label, 'Người lập': d.createdBy, 'Người duyệt': d.approvedBy || '',
  })));

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader
          title={fixedType === 'count' ? 'Kiểm kho' : 'Phiếu kho'}
          desc={fixedType === 'count' ? 'Các đợt kiểm kê — chênh lệch thừa/thiếu chỉ điều chỉnh tồn khi phiếu kiểm được duyệt'
            : 'Mọi chứng từ nhập, xuất, chuyển, hủy, kiểm — tồn kho chỉ thay đổi khi phiếu được duyệt'}
          extra={<><Button icon={<Download size={14} />} onClick={doExport}>Xuất Excel</Button>
            {fixedType === 'count' ? <Button type="primary" onClick={() => navigate('/import-export/create-inventory-count')}>Tạo phiếu kiểm</Button> : <CreateDocButton />}</>} />

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm">
          <div className="px-4">
            <Tabs activeKey={tab} onChange={k => go({ tab: k === 'all' ? null : k })}
              items={TABS.filter(t => fixedType !== 'count' || t.key !== 'transit').map(t => {
                const n = base.filter(t.match).length;
                return { key: t.key, label: <span className={t.key === 'pending' && n ? 'text-amber-600 font-semibold' : ''}>{t.label} ({n})</span> };
              })} />
          </div>
          <div className="px-4 pb-3 flex flex-wrap items-center gap-2">
            <Input allowClear className="!w-full sm:!w-64" prefix={<Search size={14} className="text-gray-400" />} placeholder="Mã phiếu, khách, NCC, mặt hàng, số lô"
              value={q} onChange={e => go({ q: e.target.value || null })} />
            {!fixedType && (
              <Select allowClear className="w-full sm:w-[180px]" placeholder="Loại phiếu" value={type || undefined} onChange={v => go({ type: v || null })}
                options={(Object.keys(DOC_TYPE) as DocType[]).map(k => ({ value: k, label: DOC_TYPE[k].label }))} />
            )}
            <Select allowClear className="w-full sm:w-[220px]" placeholder="Kho" value={wh || undefined} onChange={v => go({ wh: v || null })}
              options={s.warehouses.map(w => ({ value: w.id, label: w.name }))} />
            <Segmented value={range} onChange={v => go({ range: v === '30' ? null : String(v) })}
              options={[{ value: '7', label: '7 ngày' }, { value: '30', label: '30 ngày' }, { value: '90', label: '90 ngày' }, { value: 'all', label: 'Tất cả' }]} />
          </div>
          <Table<StockDoc> rowKey="id" size="middle" columns={columns} dataSource={rows} scroll={{ x: 1100 }}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            rowClassName={d => `cursor-pointer ${d.status === 'cancelled' ? 'opacity-50' : ''}`}
            onRow={d => ({ onClick: () => navigate(docUrl(d.id)) })} />
        </div>
      </div>
    </Layout>
  );
}

export function CountsPage() {
  return <DocsPage fixedType="count" />;
}
